import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FiArrowRight, FiCheck } from 'react-icons/fi';
import dogImage from '../assets/images-dog.jpg';
import './WellnessStorySection.css';

gsap.registerPlugin(ScrollTrigger);

const benefits = [
  { title: 'High-Quality, Natural Ingredients', description: 'Sourced locally and processed with clinical integrity.' },
  { title: 'Veterinarian Inspired Formulas', description: 'Expert wellness solutions built by pet care professionals.' },
  { title: 'Made with Love for Pets', description: 'Tested lovingly and guaranteed safe for every dog breed.' },
  { title: 'Committed to a Healthier Tomorrow', description: 'Sustainability-focused packaging and mindful sourcing.' },
];

export default function WellnessStorySection() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const motion = gsap.matchMedia();

    motion.add('(prefers-reduced-motion: no-preference)', context => {
      const imageFrame = sectionRef.current.querySelector('.wellness-story__image-frame');
      gsap.fromTo(imageFrame,
        { scale: 0, transformOrigin: '50% 50%', transition: 'none' },
        {
          scale: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: sectionRef.current.querySelector('.wellness-story__media'),
            start: 'top 95%',
            end: 'top 45%',
            scrub: .35,
            invalidateOnRefresh: true,
          },
        }
      );

      if (!('IntersectionObserver' in window)) return undefined;

      context.add('welcome', elements => {
        elements.forEach((element, index) => {
          gsap.fromTo(element,
            { opacity: 0, y: 22 },
            { opacity: 1, y: 0, duration: .65,
              delay: index * .09, ease: 'power2.out', clearProps: 'opacity,transform' }
          );
        });
      });

      const observer = new IntersectionObserver(entries => {
        const visible = entries.filter(entry => entry.isIntersecting);
        visible.forEach(entry => observer.unobserve(entry.target));
        context.welcome(visible.map(entry => entry.target));
      }, { threshold: .15 });

      sectionRef.current.querySelectorAll(
        '.wellness-story__copy > *, .wellness-story__benefit'
      ).forEach(element => observer.observe(element));

      return () => observer.disconnect();
    }, sectionRef);

    return () => motion.revert();
  }, []);

  return (
    <section className="wellness-story" ref={sectionRef} aria-labelledby="wellness-story-heading">
      <div className="header-container wellness-story__layout">
        <div className="wellness-story__copy">
          <p className="wellness-story__eyebrow">WHOLE-BODY WELLNESS</p>
          <h2 id="wellness-story-heading">More Than<br />Supplements,<br />A Healthier Tomorrow</h2>
          <p className="wellness-story__description">At Trillium Paws, we're committed to providing high-quality nutrition and care products that support your dog's health, happiness, and wellbeing — today and for years to come.</p>
          <a className="wellness-story__button" href="/about">OUR STORY <FiArrowRight aria-hidden="true" /></a>
        </div>
        <div className="wellness-story__media">
          <div className="wellness-story__image-frame">
            <img className="wellness-story__dog" src={dogImage} alt="Golden retriever enjoying a chew" width="270" height="270" loading="lazy" />
          </div>
        </div>
        <ul className="wellness-story__benefits">
          {benefits.map(({ title, description }) => (
            <li className="wellness-story__benefit" key={title}>
              <span className="wellness-story__check"><FiCheck aria-hidden="true" /></span>
              <div><h3>{title}</h3><p>{description}</p></div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
